import React, { useState } from 'react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui-elements';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUpdateFlow } from '@workspace/api-client-react';
import { cn } from '@/lib/utils';
import { fmtDateTime } from '@/lib/dateFormat';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import { Link } from 'wouter';
import {
  Plus,
  Play,
  Pause,
  Trash2,
  CheckCircle,
  AlertCircle,
  Loader2,
  Clock,
  ChevronDown,
  ChevronUp,
  Disc,
  X,
  Settings2,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface ShopifyStore {
  id: string;
  nickname: string;
}

interface WPFacets {
  brands?: string[];
  diameters?: (string | number)[];
  finishes?: string[];
  boltPatterns?: string[];
  widths?: (string | number)[];
}

interface WPSyncConfig {
  brands?: string[];
  diameter?: string;
  finish?: string;
  boltPattern?: string;
  markupPercent?: number;
  priceSource?: 'nip' | 'map' | 'msrp';
}

interface SyncFlow {
  id: string;
  name: string;
  actionType: string;
  config: WPSyncConfig;
  schedule: string | null;
  isActive: boolean;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  destStore: { id: string; nickname: string } | null;
  createdAt: string;
}

interface RunLog {
  id: string;
  flowId: string;
  status: 'running' | 'success' | 'error';
  itemsCount: number;
  message: string | null;
  createdAt: string;
}

type PhaseState = 'pending' | 'running' | 'done' | 'error';

interface Phase {
  key: 'products' | 'inventory' | 'pricing';
  label: string;
  state: PhaseState;
  count?: number;
  error?: string;
}

interface ProgressState {
  phase: 'products' | 'inventory' | 'pricing';
  done: number;
  total: number;
}

const PHASE_LABELS: Record<string, string> = {
  products: 'Sync Products',
  inventory: 'Sync Inventory',
  pricing: 'Update Pricing',
};

const SCHEDULE_OPTIONS = [
  { value: '', label: 'Manual only' },
  { value: '0 * * * *', label: 'Every hour' },
  { value: '0 6 * * *', label: 'Daily at 6 AM' },
  { value: '0 6 * * 1', label: 'Weekly (Mon 6 AM)' },
];

function StatusBadge({ status }: { status: string | null }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-card text-muted-foreground">
        Never run
      </span>
    );
  }
  if (status === 'success') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        Success
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-500/15 text-red-400 border border-red-500/20">
        <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
        Error
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-400 border border-amber-500/20">
      <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
      {status}
    </span>
  );
}

function FilterChip({ label }: { label: string }) {
  return (
    <span className="px-2 py-0.5 rounded text-xs bg-primary/10 text-primary/80 border border-primary/20 font-mono">
      {label}
    </span>
  );
}

interface PhaseDetail {
  created?: number;
  updated?: number;
  errors?: number;
  fetched?: number;
  updated_inv?: number;
  skipped?: number;
  itemsCount?: number;
  error?: string;
}

function formatPhaseDetail(phase: string, d: PhaseDetail): string {
  if (d.error) return d.error;
  if (phase === 'products') {
    const parts: string[] = [];
    if (d.created !== undefined) parts.push(`${d.created} created`);
    if (d.updated !== undefined) parts.push(`${d.updated} updated`);
    if (d.errors) parts.push(`${d.errors} errors`);
    return parts.length > 0 ? parts.join(', ') : `${d.itemsCount ?? 0} synced`;
  }
  if (phase === 'inventory' || phase === 'pricing') {
    const parts: string[] = [];
    const updVal = d.updated ?? d.updated_inv;
    if (updVal !== undefined) parts.push(`${updVal} updated`);
    if (d.skipped) parts.push(`${d.skipped} skipped`);
    if (d.errors) parts.push(`${d.errors} errors`);
    return parts.length > 0 ? parts.join(', ') : `${d.itemsCount ?? 0} updated`;
  }
  return `${d.itemsCount ?? 0} items`;
}

function RunHistoryRow({ log }: { log: RunLog }) {
  const [expanded, setExpanded] = useState(false);
  let parsedMessage: Record<string, unknown> | null = null;
  try {
    if (log.message) parsedMessage = JSON.parse(log.message);
  } catch {}

  const hasPhases = parsedMessage && (parsedMessage['products'] || parsedMessage['inventory'] || parsedMessage['pricing']);

  // Build a concise summary line from products detail (created/updated)
  const productDetail = parsedMessage?.['products'] as PhaseDetail | undefined;
  const summary = productDetail && !productDetail.error
    ? formatPhaseDetail('products', productDetail)
    : log.itemsCount > 0
    ? `${log.itemsCount} items`
    : '—';

  return (
    <div className="border-b border-border/50 last:border-0">
      <div className="flex items-center gap-4 py-2 px-3 text-xs">
        <span className="text-muted-foreground w-28 flex-shrink-0">
          {fmtDateTime(log.createdAt)}
        </span>
        <StatusBadge status={log.status} />
        <span className="text-muted-foreground flex-1">{summary}</span>
        {hasPhases && (
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
      {expanded && parsedMessage && (
        <div className="px-3 pb-2 grid grid-cols-3 gap-2">
          {(['products', 'inventory', 'pricing'] as const).map(phase => {
            const d = parsedMessage![phase] as PhaseDetail | undefined;
            if (!d) return null;
            return (
              <div key={phase} className={cn(
                'rounded p-2 border',
                d.error
                  ? 'bg-red-500/5 border-red-500/20'
                  : 'bg-muted/30 border-border/50'
              )}>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">
                  {PHASE_LABELS[phase]}
                </p>
                <p className={cn('text-xs', d.error ? 'text-red-400' : 'text-foreground')}>
                  {formatPhaseDetail(phase, d)}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SyncJobCard({
  flow,
  onDelete,
  onRefreshFlows,
}: {
  flow: SyncFlow;
  onDelete: (id: string) => void;
  onRefreshFlows: () => void;
}) {
  const authOpts = useAuthHeaders();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [phases, setPhases] = useState<Phase[]>([
    { key: 'products', label: 'Sync Products', state: 'pending' },
    { key: 'inventory', label: 'Sync Inventory', state: 'pending' },
    { key: 'pricing', label: 'Update Pricing', state: 'pending' },
  ]);
  const [runError, setRunError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [runStartTime, setRunStartTime] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const updateFlow = useUpdateFlow({
    request: authOpts,
    mutation: {
      onSuccess: () => {
        onRefreshFlows();
      },
      onError: () => {
        toast({ title: 'Failed to update job', variant: 'destructive' });
      },
    },
  });

  const handleTogglePause = () => {
    updateFlow.mutate({ id: flow.id, data: { isActive: !flow.isActive } });
  };

  const { data: logs = [], isLoading: logsLoading } = useQuery<RunLog[]>({
    queryKey: ['wp-sync-logs', flow.id],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/flows/${flow.id}/logs?limit=10`, { headers: authOpts.headers });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: historyOpen,
    staleTime: 15_000,
  });

  const config = flow.config;
  const filterChips: string[] = [];
  if (config.brands && config.brands.length > 0) filterChips.push(...config.brands.map(b => `Brand: ${b}`));
  if (config.diameter) filterChips.push(`Ø ${config.diameter}"`);
  if (config.finish) filterChips.push(`Finish: ${config.finish}`);
  if (config.boltPattern) filterChips.push(`BP: ${config.boltPattern}`);
  if (config.markupPercent) filterChips.push(`+${config.markupPercent}% markup`);

  React.useEffect(() => {
    if (!running || runStartTime === null) return;
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - runStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [running, runStartTime]);

  const handleRunNow = async () => {
    const start = Date.now();
    setRunning(true);
    setRunError(null);
    setProgress(null);
    setRunStartTime(start);
    setElapsed(0);
    setPhases([
      { key: 'products', label: 'Sync Products', state: 'running' },
      { key: 'inventory', label: 'Sync Inventory', state: 'pending' },
      { key: 'pricing', label: 'Update Pricing', state: 'pending' },
    ]);

    let currentPhaseIdx = 0;

    try {
      const res = await fetch(`${BASE}/api/flows/${flow.id}/run-stream`, {
        method: 'POST',
        headers: authOpts.headers,
      });

      if (!res.ok || !res.body) {
        const d = await res.json().catch(() => ({ error: `Error ${res.status}` }));
        throw new Error(d.error ?? 'Run failed');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(line.slice(6));

            if (ev.type === 'progress') {
              setProgress({ phase: ev.phase, done: ev.done, total: ev.total });
            }

            if (ev.type === 'phase_done') {
              const phaseKey = ev.phase as Phase['key'];
              const nextPhaseIdx = currentPhaseIdx + 1;
              setProgress(null);
              setPhases(prev => prev.map((p, i) => {
                if (p.key === phaseKey) {
                  return { ...p, state: ev.error ? 'error' : 'done', count: ev.itemsCount, error: ev.error };
                }
                if (i === nextPhaseIdx) {
                  return { ...p, state: 'running' };
                }
                return p;
              }));
              currentPhaseIdx++;
            }

            if (ev.type === 'done') {
              const terminalState: PhaseState = ev.status === 'error' ? 'error' : 'done';
              setPhases(prev => prev.map(p => p.state === 'running' ? { ...p, state: terminalState } : p));
              setProgress(null);
              if (ev.status === 'error' && ev.error) {
                setRunError(ev.error as string);
              }
              onRefreshFlows();
              queryClient.invalidateQueries({ queryKey: ['wp-sync-logs', flow.id] });
            }
          } catch {}
        }
      }
    } catch (err: any) {
      setRunError(err.message);
      setPhases(prev => prev.map(p => p.state === 'running' ? { ...p, state: 'error' } : p));
    } finally {
      setRunning(false);
      setRunStartTime(null);
    }
  };

  return (
    <div className={cn(
      'glass-panel rounded-2xl border transition-all',
      flow.isActive ? 'border-border hover:border-primary/20' : 'border-border/50 opacity-60'
    )}>
      {/* Card header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-bold text-foreground truncate">{flow.name}</h3>
              {!flow.isActive && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-400 border border-amber-500/20 flex-shrink-0">
                  <Pause className="w-3 h-3" />
                  Paused
                </span>
              )}
              <StatusBadge status={flow.lastRunStatus} />
            </div>

            <p className="text-sm text-muted-foreground mb-3">
              <span className="font-medium text-foreground">{flow.destStore?.nickname ?? 'Unknown store'}</span>
              {flow.lastRunAt && (
                <span className="ml-3 text-xs text-muted-foreground">
                  Last run {fmtDateTime(flow.lastRunAt)}
                </span>
              )}
            </p>

            {/* Filter chips */}
            {filterChips.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {filterChips.map(chip => <FilterChip key={chip} label={chip} />)}
              </div>
            ) : (
              <span className="text-xs text-slate-600 italic">No filters — syncs full catalog</span>
            )}
          </div>

          {/* Schedule + actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {flow.schedule ? (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted/50 border border-border/60 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5 text-emerald-400" />
                <span className="font-mono">{flow.schedule}</span>
              </div>
            ) : (
              <span className="text-xs text-slate-600 italic">Manual</span>
            )}

            <Button
              variant="secondary"
              size="sm"
              className="gap-1.5"
              onClick={handleRunNow}
              disabled={running || !flow.isActive}
              title={!flow.isActive ? 'Resume job to run it' : undefined}
            >
              {running ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running…</>
              ) : (
                <><Play className="w-3.5 h-3.5" /> Run Now</>
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className={cn(
                flow.isActive
                  ? 'text-muted-foreground hover:text-amber-400 hover:bg-amber-500/10'
                  : 'text-amber-400 hover:text-emerald-400 hover:bg-emerald-500/10'
              )}
              onClick={handleTogglePause}
              disabled={running || updateFlow.isPending}
              title={flow.isActive ? 'Pause job' : 'Resume job'}
            >
              {flow.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
              onClick={() => onDelete(flow.id)}
              disabled={running}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Live progress */}
        {running && (
          <div className="mt-4 space-y-3">
            {/* Phase indicators */}
            <div className="flex items-center gap-3">
              {phases.map(phase => (
                <div key={phase.key} className="flex items-center gap-1.5 text-xs">
                  {phase.state === 'pending' && (
                    <div className="w-3.5 h-3.5 rounded-full border-2 border-border flex-shrink-0" />
                  )}
                  {phase.state === 'running' && (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-primary flex-shrink-0" />
                  )}
                  {phase.state === 'done' && (
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                  )}
                  {phase.state === 'error' && (
                    <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                  )}
                  <span className={cn(
                    phase.state === 'pending' ? 'text-slate-600' :
                    phase.state === 'running' ? 'text-primary' :
                    phase.state === 'done' ? 'text-emerald-400' : 'text-red-400'
                  )}>
                    {phase.label}
                    {phase.count !== undefined && phase.state === 'done' && (
                      <span className="ml-1 text-muted-foreground">({phase.count})</span>
                    )}
                  </span>
                  {phases.indexOf(phase) < phases.length - 1 && (
                    <div className="w-4 h-px bg-muted mx-1" />
                  )}
                </div>
              ))}
            </div>

            {/* Progress bar — shown when products phase is active and we have a total */}
            {progress && progress.total > 0 && (() => {
              const pct = Math.min(100, Math.round((progress.done / progress.total) * 100));
              const rate = elapsed > 0 ? progress.done / elapsed : 0;
              const remaining = rate > 0 ? Math.round((progress.total - progress.done) / rate) : null;
              const fmtTime = (s: number) => s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
              return (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-medium tabular-nums">
                      {progress.done.toLocaleString()} / {progress.total.toLocaleString()} products
                      <span className="ml-2 text-slate-600">({pct}%)</span>
                    </span>
                    <span className="text-muted-foreground tabular-nums">
                      {elapsed > 0 && `${fmtTime(elapsed)} elapsed`}
                      {remaining !== null && remaining > 0 && ` · ~${fmtTime(remaining)} left`}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Elapsed time when no detailed progress yet */}
            {(!progress || progress.total === 0) && elapsed > 0 && (
              <div className="text-xs text-muted-foreground tabular-nums">
                {elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`} elapsed
              </div>
            )}
          </div>
        )}

        {runError && (
          <div className="mt-3 flex items-center gap-2 text-xs text-red-400 bg-red-500/10 rounded-lg px-3 py-2 border border-red-500/20">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {runError}
          </div>
        )}
      </div>

      {/* Run history toggle */}
      <div className="border-t border-border/50">
        <button
          onClick={() => setHistoryOpen(v => !v)}
          className="w-full flex items-center justify-between px-5 py-2.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <span>Run history</span>
          {historyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>

        {historyOpen && (
          <div className="px-2 pb-2">
            {logsLoading ? (
              <div className="text-xs text-muted-foreground text-center py-4">Loading…</div>
            ) : logs.length === 0 ? (
              <div className="text-xs text-slate-600 text-center py-4 italic">No runs yet</div>
            ) : (
              <div className="bg-muted/50 rounded-xl overflow-hidden border border-border/50">
                {logs.map(log => <RunHistoryRow key={log.id} log={log} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type Step = 'store' | 'filters' | 'schedule';

interface FormState {
  storeId: string;
  name: string;
  brands: string[];
  diameter: string;
  finish: string;
  boltPattern: string;
  markupPercent: string;
  priceSource: 'nip' | 'map' | 'msrp';
  schedule: string;
}

function CreateSyncJobModal({
  stores,
  facets,
  facetsLoading,
  onClose,
  onCreate,
}: {
  stores: ShopifyStore[];
  facets: WPFacets;
  facetsLoading: boolean;
  onClose: () => void;
  onCreate: (form: FormState) => Promise<void>;
}) {
  const [step, setStep] = useState<Step>('store');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>({
    storeId: stores.length === 1 ? stores[0]!.id : '',
    name: '',
    brands: [],
    diameter: '',
    finish: '',
    boltPattern: '',
    markupPercent: '0',
    priceSource: 'nip',
    schedule: '',
  });

  const set = (key: keyof FormState, val: string) =>
    setForm(prev => ({ ...prev, [key]: val }));

  const toggleBrand = (brand: string) => {
    setForm(prev => ({
      ...prev,
      brands: prev.brands.includes(brand)
        ? prev.brands.filter(b => b !== brand)
        : [...prev.brands, brand],
    }));
  };

  const selectedStore = stores.find(s => s.id === form.storeId);

  const canGoNext = step === 'store'
    ? !!form.storeId && !!form.name.trim()
    : step === 'filters'
    ? true
    : true;

  const handleNext = () => {
    if (step === 'store') setStep('filters');
    else if (step === 'filters') setStep('schedule');
    else handleCreate();
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await onCreate(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const steps: Step[] = ['store', 'filters', 'schedule'];
  const stepIdx = steps.indexOf(step);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border/60">
          <div>
            <h2 className="text-lg font-bold text-foreground">Create Sync Job</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Step {stepIdx + 1} of {steps.length}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex items-center gap-1 px-6 pt-4">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all',
                i < stepIdx ? 'bg-primary text-black' :
                i === stepIdx ? 'bg-primary/20 text-primary border border-primary/40' :
                'bg-muted/50 text-slate-600'
              )}>
                {i < stepIdx ? '✓' : i + 1}
              </div>
              {i < steps.length - 1 && (
                <div className={cn('flex-1 h-px transition-colors', i < stepIdx ? 'bg-primary/50' : 'bg-muted/60')} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step content */}
        <div className="px-6 py-5 space-y-4">
          {step === 'store' && (
            <>
              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Sync Job Name</label>
                <input
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="e.g. Fuel Wheels Nightly Sync"
                  className="w-full px-3 py-2.5 rounded-xl bg-muted/50 border border-border text-sm text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                  autoFocus
                />
              </div>
              {stores.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No stores connected. Add a store first.</p>
              ) : (
                <ShopifyTargetStoreSelector
                  stores={stores}
                  value={form.storeId}
                  onChange={(storeId) => set('storeId', storeId)}
                  label="Destination Shopify Store"
                  variant="tiles"
                />
              )}
            </>
          )}

          {step === 'filters' && (
            <>
              <p className="text-xs text-muted-foreground">
                Leave filters empty to sync the full Wheel Pros catalog.
              </p>

              {/* Brand multi-select */}
              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Brands</label>
                {facetsLoading ? (
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading brands…
                  </div>
                ) : facets.brands && facets.brands.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto custom-scrollbar">
                    {facets.brands.map(brand => (
                      <button
                        key={brand}
                        onClick={() => toggleBrand(brand)}
                        className={cn(
                          'px-2.5 py-1 rounded-lg text-xs border transition-all',
                          form.brands.includes(brand)
                            ? 'bg-primary/15 text-primary border-primary/40'
                            : 'bg-muted/30 text-muted-foreground border-border/60 hover:border-border'
                        )}
                      >
                        {brand}
                      </button>
                    ))}
                  </div>
                ) : (
                  <input
                    value={form.brands[0] ?? ''}
                    onChange={e => setForm(prev => ({ ...prev, brands: e.target.value ? [e.target.value] : [] }))}
                    placeholder="e.g. Fuel, KMC"
                    className="w-full px-3 py-2 rounded-xl bg-muted/50 border border-border text-sm text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                  />
                )}
                {form.brands.length > 0 && (
                  <p className="text-xs text-primary mt-1">{form.brands.length} brand(s) selected</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Diameter (inches)</label>
                  <select
                    value={form.diameter}
                    onChange={e => set('diameter', e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  >
                    <option value="">Any</option>
                    {(facets.diameters ?? []).map(d => (
                      <option key={d} value={String(d)}>{d}"</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Finish</label>
                  <select
                    value={form.finish}
                    onChange={e => set('finish', e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  >
                    <option value="">Any</option>
                    {(facets.finishes ?? []).map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Bolt Pattern</label>
                <select
                  value={form.boltPattern}
                  onChange={e => set('boltPattern', e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                >
                  <option value="">Any</option>
                  {(facets.boltPatterns ?? []).map(bp => (
                    <option key={bp} value={bp}>{bp}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {step === 'schedule' && (
            <>
              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Markup Percentage</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="500"
                    step="0.5"
                    value={form.markupPercent}
                    onChange={e => set('markupPercent', e.target.value)}
                    className="w-28 px-3 py-2 rounded-xl bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  />
                  <span className="text-sm text-muted-foreground">% above cost (NIP)</span>
                </div>
                <p className="text-xs text-slate-600 mt-1">Applied on top of the dealer NIP price from Wheel Pros.</p>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Price Source</label>
                <select
                  value={form.priceSource}
                  onChange={e => set('priceSource', e.target.value as 'nip' | 'map' | 'msrp')}
                  className="w-full px-3 py-2 rounded-xl bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                >
                  <option value="nip">NIP (Dealer cost) — recommended</option>
                  <option value="map">MAP (Minimum Advertised Price)</option>
                  <option value="msrp">MSRP</option>
                </select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Sync Schedule</label>
                <div className="space-y-2">
                  {SCHEDULE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => set('schedule', opt.value)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all text-left',
                        form.schedule === opt.value
                          ? 'border-primary/50 bg-primary/10 text-primary'
                          : 'border-border bg-muted/30 text-foreground hover:border-border'
                      )}
                    >
                      <div className={cn(
                        'w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center',
                        form.schedule === opt.value ? 'border-primary bg-primary' : 'border-border'
                      )}>
                        {form.schedule === opt.value && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                      </div>
                      <span className="text-sm">{opt.label}</span>
                      {opt.value && (
                        <span className="ml-auto font-mono text-xs text-muted-foreground">{opt.value}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-muted/30 rounded-xl border border-border/60 p-4 space-y-1.5">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Summary</p>
                <p className="text-sm text-foreground">
                  <span className="text-muted-foreground">Store:</span> {selectedStore?.nickname}
                </p>
                {form.brands.length > 0 && (
                  <p className="text-sm text-foreground">
                    <span className="text-muted-foreground">Brands:</span> {form.brands.join(', ')}
                  </p>
                )}
                {form.diameter && (
                  <p className="text-sm text-foreground">
                    <span className="text-muted-foreground">Diameter:</span> {form.diameter}"
                  </p>
                )}
                {form.finish && (
                  <p className="text-sm text-foreground">
                    <span className="text-muted-foreground">Finish:</span> {form.finish}
                  </p>
                )}
                <p className="text-sm text-foreground">
                  <span className="text-muted-foreground">Markup:</span> {form.markupPercent}%
                </p>
                <p className="text-sm text-foreground">
                  <span className="text-muted-foreground">Schedule:</span>{' '}
                  {SCHEDULE_OPTIONS.find(o => o.value === form.schedule)?.label ?? 'Manual only'}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border/60">
          <button
            onClick={stepIdx > 0 ? () => setStep(steps[stepIdx - 1]!) : onClose}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {stepIdx > 0 ? 'Back' : 'Cancel'}
          </button>
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!canGoNext || saving}
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</>
            ) : step === 'schedule' ? (
              'Create Sync Job'
            ) : (
              'Next →'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function WheelProsSync() {
  const { token } = useAuthStore();
  const authOpts = useAuthHeaders();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const accountQueryScope = React.useMemo(() => crypto.randomUUID(), [token]);

  const { data: status } = useQuery<{ configured: boolean; connected?: boolean; error?: string }>({
    queryKey: ['wheelpros-status', accountQueryScope],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/wheelpros/status`, { headers: authOpts.headers });
      return r.json();
    },
    enabled: !!token,
    staleTime: 60_000,
  });

  const { data: stores = [] } = useQuery<ShopifyStore[]>({
    queryKey: ['stores', 'wp-sync', accountQueryScope],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/stores`, { headers: authOpts.headers });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
    enabled: !!token,
  });

  const { data: flows = [], isLoading, refetch: refetchFlows } = useQuery<SyncFlow[]>({
    queryKey: ['wp-sync-flows', accountQueryScope],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/flows`, { headers: authOpts.headers });
      if (!r.ok) throw new Error('Failed');
      const all: SyncFlow[] = await r.json();
      return all.filter(f => (f.actionType as string).startsWith('wp_'));
    },
    enabled: !!token,
    staleTime: 30_000,
  });

  const { data: facets = {}, isLoading: facetsLoading } = useQuery<WPFacets>({
    queryKey: ['wp-facets', accountQueryScope],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/wheelpros/facets`, { headers: authOpts.headers });
      if (!r.ok) return {};
      return r.json();
    },
    enabled: !!token && showCreate,
    staleTime: 3_600_000,
  });

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this sync job? This cannot be undone.')) return;
    try {
      const r = await fetch(`${BASE}/api/flows/${id}`, {
        method: 'DELETE',
        headers: authOpts.headers,
      });
      if (!r.ok) throw new Error(`Error ${r.status}`);
      toast({ title: 'Sync job deleted' });
      refetchFlows();
    } catch (err: any) {
      toast({ title: 'Delete failed', description: err.message, variant: 'destructive' });
    }
  };

  const handleCreate = async (form: FormState) => {
    const config: WPSyncConfig = {
      brands: form.brands.length > 0 ? form.brands : undefined,
      diameter: form.diameter || undefined,
      finish: form.finish || undefined,
      boltPattern: form.boltPattern || undefined,
      markupPercent: parseFloat(form.markupPercent) || 0,
      priceSource: form.priceSource,
    };

    const body = {
      name: form.name,
      sourceStoreId: form.storeId,
      destStoreId: form.storeId,
      actionType: 'wp_sync_all',
      config,
      schedule: form.schedule || null,
      isActive: true,
    };

    const r = await fetch(`${BASE}/api/flows`, {
      method: 'POST',
      headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.message ?? `Error ${r.status}`);
    }

    toast({ title: 'Sync job created', description: `"${form.name}" is ready to run.` });
    refetchFlows();
  };

  const configured = status?.configured;
  const connected = status?.connected;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-gradient">WP Auto-Sync</h1>
            <p className="text-muted-foreground mt-1">
              Automated Wheel Pros → Shopify sync jobs: products, inventory, and pricing.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {configured && (
              <div className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border',
                connected
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
              )}>
                <div className={cn('w-1.5 h-1.5 rounded-full', connected ? 'bg-emerald-400' : 'bg-amber-400')} />
                {connected ? 'WP Connected' : 'WP Auth issue'}
              </div>
            )}
            <Button
              variant="primary"
              onClick={() => setShowCreate(true)}
              disabled={!configured || !connected || stores.length === 0}
              className="gap-2"
            >
              <Plus className="w-4 h-4" /> Create Sync Job
            </Button>
          </div>
        </div>

        {/* Credential banner */}
        {!configured && (
          <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
            <Settings2 className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-300">Wheel Pros credentials not configured</p>
              <p className="text-xs text-amber-400/80 mt-0.5">
                Connect your own Wheel Pros account in{' '}
                <Link href="/settings/wheel-pros" className="underline font-medium">WheelPros Account settings</Link>.
                {' '}Credentials are saved securely for your SoraFlo account only.
              </p>
            </div>
          </div>
        )}

        {configured && !connected && status?.error && (
          <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-700 dark:text-red-300">Wheel Pros authentication failed</p>
              <p className="text-xs text-red-400/80 mt-0.5">{status.error}</p>
            </div>
          </div>
        )}

        {stores.length === 0 && configured && (
          <div className="flex items-start gap-3 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700 dark:text-blue-300">Connect a Shopify store first before creating sync jobs.</p>
          </div>
        )}

        {/* Flow list */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2].map(i => (
              <div key={i} className="h-32 bg-muted/30 rounded-2xl animate-pulse border border-border/50" />
            ))}
          </div>
        ) : flows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted/50 border border-border/60 flex items-center justify-center mb-4">
              <Disc className="w-8 h-8 text-slate-600" />
            </div>
            <p className="text-muted-foreground font-medium text-lg">No sync jobs yet</p>
            <p className="text-slate-600 text-sm mt-1 max-w-sm">
              {configured && connected
                ? 'Create your first sync job to automatically keep your Shopify store stocked with Wheel Pros products.'
                : 'Configure your Wheel Pros credentials and connect a Shopify store to get started.'}
            </p>
            {configured && connected && stores.length > 0 && (
              <Button
                variant="primary"
                className="mt-6 gap-2"
                onClick={() => setShowCreate(true)}
              >
                <Plus className="w-4 h-4" /> Create your first sync job
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {flows.map(flow => (
              <SyncJobCard
                key={flow.id}
                flow={flow}
                onDelete={handleDelete}
                onRefreshFlows={() => refetchFlows()}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <CreateSyncJobModal
          stores={stores}
          facets={facets}
          facetsLoading={facetsLoading}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}
    </Layout>
  );
}
